// next/navigation compat shim — P2 REAL port (not the throwaway probe stub).
// Adapts the Next.js App Router client-navigation hooks that reused apps/web
// files call (`usePathname`, `useRouter`, `useSearchParams`, `useParams`,
// `redirect`, `notFound`) onto TanStack Router's real hooks, so files ported
// under `@/*` keep working unmodified (per FRONTEND_SPA_DESIGN.md §5c: "Ship
// a src/compat/next-navigation.ts shim first so files port unmodified, then
// burn the shim down file-by-file").
//
// Routing note: `router.push`/`replace` accept an arbitrary Next-style URL
// string (path + querystring + hash), which TanStack's typed `to` param
// can't accept directly. We use TanStack's `href` escape hatch instead
// (NavigateOptions.href — "navigate to a fully built href", untyped
// string) so this shim needs no per-caller parsing or route-tree knowledge.
import {
  useLocation,
  useNavigate,
  useParams as useTanstackParams,
  useRouter as useTanstackRouter,
  redirect as tanstackRedirect,
  notFound as tanstackNotFound,
} from '@tanstack/react-router';

// Next's router.push/replace accept an optional second `{ scroll?: boolean }`
// options arg (e.g. lab-chat-view.tsx:2568 `router.replace('/', { scroll:
// false })`, used to null out a `?q=` prefill without re-triggering scroll
// restoration). TanStack has no matching per-call scroll knob (scroll
// restoration is router-wide, via <ScrollRestoration>), so we accept and
// intentionally drop the option rather than throw — parity gap is scroll
// position only, not navigation correctness.
type NextRouterOptions = { scroll?: boolean };

export function useRouter() {
  const navigate = useNavigate();
  const router = useTanstackRouter();
  return {
    push: (href: string, _options?: NextRouterOptions) => navigate({ href }),
    replace: (href: string, _options?: NextRouterOptions) => navigate({ href, replace: true }),
    back: () => router.history.back(),
    forward: () => router.history.forward(),
    refresh: () => router.invalidate(),
    prefetch: (_href: string) => {},
  };
}

export function usePathname(): string {
  return useLocation({ select: (l) => l.pathname });
}

export function useSearchParams(): URLSearchParams {
  const searchStr = useLocation({ select: (l) => l.searchStr });
  return new URLSearchParams(searchStr);
}

// Next's useParams() returns the merged dynamic-segment params of the whole
// matched route stack with no `from` required. `strict: false` is the
// TanStack equivalent (loosely-typed union of every matched route's params).
export function useParams<T = Record<string, string>>(): T {
  return useTanstackParams({ strict: false }) as unknown as T;
}

// Next's redirect()/notFound() are dual-mode: thrown during render (Server
// Components) OR called imperatively in client code. Our reused files only
// hit the client-imperative path (server shells stay behind in apps/web).
// Throwing TanStack's redirect()/notFound() objects works from a route
// `beforeLoad`/`loader`; called elsewhere (inside a component body) it's
// caught by the nearest CatchBoundary, matching the "unmounts and redirects"
// behavior callers already expect.
export function redirect(url: string): never {
  throw tanstackRedirect({ href: url });
}

export function notFound(): never {
  throw tanstackNotFound();
}

// Next's App Router parallel/intercepting-route helper. Nothing in the SPA's
// flat route tree uses route groups/parallel routes — always null, matching
// what every reused call site already tolerates (optional chaining today).
export function useSelectedLayoutSegment(): string | null {
  return null;
}
